/**
 * AtlasVROptimizerModule — VR-only render fixes, a per-headset quality profile, the in-headset performance numbers
 * and a frame-rate governor, applied for the lifetime of an immersive-vr session and fully restored on exit.
 * Desktop behaviour is untouched: every change is edge-triggered on the session boundary and every touched value is
 * snapshotted first, so outside a session this module is inert.
 *
 * WHY EACH FIX (Quest 3 field report: dark ground, a sliver of sky, pitch-up glitching — all three trace to the
 * same render-state problems):
 *
 * 1. CLEAR THE XR FRAMEBUFFER. EngineRenderer sets `autoClear = false` once, globally; the desktop path compensates
 *    because post-processing's PassNode force-clears around the scene render, but the engine BYPASSES post while
 *    `xr.isPresenting` and issues a bare render — so the XR depth buffer accumulates from frame 1 ("can look down,
 *    can't look back up"). Fix: `autoClear = true` while presenting (restored on exit), enforced by a
 *    Render/PreUpdate system in case any other code flips it mid-session.
 *
 * 2. VR DEPTH RANGE. immersive-vr runs the WebGL2 backend ([-1,1] NDC, no reversed-z). Depth error at distance z
 *    goes as z²/near — the NEAR plane is the lever (CLAUDE.md: a raw depth threshold is a distance threshold), and a
 *    far plane ≫ near barely moves it. VR doubles the desktop's near to 0.1 m and leaves the far plane to the terrain
 *    far tier (120 km). The first clamp also set far 1500, which clipped the far tier and which TerrainFarTierModule
 *    re-asserted to 120 km on its next tick anyway (measured by verify:vr-optimizer:probe, 2026-09-28). three
 *    propagates the near to session.updateRenderState({depthNear}).
 *
 * 3. THE VR PROFILE (2026-09-28, XR P0.4 — lib/xr_profile.ts). One decision for EVERY TierPreset key (the old hand
 *    list covered 20 of 136), at one of three levels chosen by the headset (lib/xr_headsets.ts). It overlays the
 *    LIVE activePreset() object (never through the persisting setters) and `setPersistOverride` makes any save
 *    during the session write the DESKTOP snapshot; `restorePreset` puts the object back exactly — keys the desktop
 *    never carried are deleted, not left at the VR value. PostModule.applySettings() re-emits
 *    `atlas:quality-changed`, so Lighting (CSM teardown), Environment (foliage re-scatter, the ground key), Culling,
 *    Streaming and Atmosphere rescale.
 *
 * 4. WHAT A FRAME COSTS, IN THE HEADSET (XR P0.1 — lib/xr_perf.ts): every XR frame's interval, draw calls and
 *    triangles feed the rolling snapshot the VR panel's Perf page reads.
 *
 * 5. THE GOVERNOR (XR P1.8 — lib/xr_governor.ts): over budget → foveation (only where a projection layer exists:
 *    three foveates the scene render on the layers path alone), then a lower display rate, then a cheaper VR level
 *    (rationed — a level change rebuilds); headroom walks back, never past where the headset started. The
 *    framebuffer scale is fixed per session (three applies it before setSession). `?xrgov=0` turns it off.
 *
 * Framebuffer scale / foveation / display rate are chosen per headset in main.ts before setSession.
 */

import type * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { activePreset, setPersistOverride, type TierPreset } from '../lib/quality_tiers.js';
import { applyVrPreset, clonePresetShallow, restorePreset, vrChanges, clampLevel, VR_LEVEL_NAMES, type VrLevel } from '../lib/xr_profile.js';
import { headsetProfile, refineByEyeBuffer, type HeadsetId, type HeadsetProfile } from '../lib/xr_headsets.js';
import { recordXrFrame, resetXrPerf, setXrPerfContext, xrPerfSnapshot } from '../lib/xr_perf.js';
import { governorInit, governorStep, GOV, type GovernorAction, type GovernorState } from '../lib/xr_governor.js';
import type { PostModule } from './PostModule.js';

/** VR camera near plane (see WHY #2). */
const VR_NEAR = 0.1;
/** How often the governor judges the window, seconds. */
const GOV_EVERY_S = 0.5;

export interface VrBootChoice {
  /** The headset main.ts detected from the UA before any session. */
  headset: HeadsetId;
  /** The scale / foveation / rate main.ts handed the XR module (after URL overrides). */
  framebufferScale: number;
  foveation: number;
  targetHz: number;
  /** `?xrlevel=` — pin the VR level (the governor still runs unless `governor` is false). */
  level?: VrLevel;
  /** `?xrgov=0` turns the governor off. */
  governor: boolean;
}

type XRish = {
  getSession?: () => XRSessionLike | null;
  setFoveation?: (v: number) => void;
  getFoveation?: () => number | undefined;
  addEventListener: (t: string, f: () => void) => void;
};
type XRSessionLike = {
  frameRate?: number;
  supportedFrameRates?: ArrayLike<number>;
  updateTargetFrameRate?: (r: number) => Promise<void>;
  renderState?: { baseLayer?: { framebufferWidth?: number; framebufferHeight?: number; fixedFoveation?: number } };
};

export class AtlasVROptimizerModule implements IModule {
  readonly name = 'AtlasVROptimizer';

  private app!: App;
  /** Non-null exactly while a session's overrides are applied. */
  private saved: { autoClear: boolean; near: number; far: number; preset: TierPreset } | null = null;
  /** Set when the session started before the avatar rig existed. */
  private cameraClampPending = false;

  private headset: HeadsetId;
  private profile: HeadsetProfile;
  private level: VrLevel = 2;
  private startLevel: VrLevel = 2;
  private hz = 72;
  private startHz = 72;
  private foveation = 0;
  private startFoveation = 0;
  private gov: GovernorState = governorInit();
  private govAcc = 0;
  /** Wall seconds since the session started or a level changed (the governor's warm-up). */
  private warmS = 0;
  private lastFrameT = 0;
  private lastAction: (GovernorAction & { at: number }) | null = null;
  private actions = 0;

  constructor(private readonly boot: VrBootChoice = { headset: 'unknown', framebufferScale: 0.75, foveation: 1, targetHz: 72, governor: true }) {
    this.headset = boot.headset;
    this.profile = headsetProfile(boot.headset);
  }

  init(app: App): void {
    this.app = app;
    const xrm = app.renderer.native.xr as unknown as XRish;
    xrm.addEventListener('sessionstart', () => this.apply());
    xrm.addEventListener('sessionend', () => this.restore());

    // Insurance: keep the XR framebuffer clearing even if other code touches autoClear mid-session, apply the
    // camera clamp late when VR was entered before the avatar rig existed, feed the perf window and run the
    // governor. Runs before the engine's XR systems (XRCameraOverride at -100); outside a session: one branch.
    app.world.addSystem(
      makeSystem(
        'AtlasVRState',
        Phase.Render,
        -150,
        (dt: number) => {
          if (!this.saved) return;
          const r = this.app.renderer.native;
          if (!r.autoClear) r.autoClear = true;
          if (this.cameraClampPending) this.clampCamera();
          this.tickPerf(dt);
        },
        Sub.PreUpdate,
      ),
    );

    (globalThis as Record<string, unknown>).__vrOpt = () => ({
      presenting: this.saved !== null,
      headset: this.headset,
      label: this.profile.label,
      profile: { ...this.profile },
      boot: { ...this.boot },
      level: this.level,
      levelName: VR_LEVEL_NAMES[this.level],
      startLevel: this.startLevel,
      foveation: this.foveation,
      hz: this.hz,
      changes: this.saved ? vrChanges(this.saved.preset, this.level).length : 0,
      governor: { ...this.gov, enabled: this.boot.governor, actions: this.actions, lastAction: this.lastAction },
      perf: { ...xrPerfSnapshot() },
      /** Probe reads: the live preset every module draws from, and what the renderer/camera hold now. */
      presetJson: (): string => JSON.stringify(activePreset()),
      render: () => ({ autoClear: this.app.renderer.native.autoClear, near: this.camera()?.near ?? null, far: this.camera()?.far ?? null }),
      /** Probe lever: switch the VR level now (a rebuild), as the governor would. */
      setLevel: (n: number): number => { if (this.saved) this.setLevel(clampLevel(n), 'lever'); return this.level; },
    });
  }

  // ─── Session boundary ─────────────────────────────────────────────────────────

  private apply(): void {
    if (this.saved) return; // double sessionstart guard
    const r = this.app.renderer.native;
    const live = activePreset();
    this.saved = {
      autoClear: r.autoClear,
      near: this.camera()?.near ?? VR_NEAR,
      far: this.camera()?.far ?? 120_000,
      preset: clonePresetShallow(live),
    };
    // Any save during the session persists the DESKTOP values, never the headset's.
    setPersistOverride(this.saved.preset);

    // #1 — clear the XR framebuffer every frame.
    r.autoClear = true;
    // #2 — WebGL2-safe depth range (deferred until the rig exists if needed).
    this.cameraClampPending = true;
    this.clampCamera();

    // The headset, refined by the eye buffer the runtime actually handed back (an unnamed Quest UA).
    const session = this.session();
    const bl = session?.renderState?.baseLayer;
    const scale = this.boot.framebufferScale > 0 ? this.boot.framebufferScale : 1;
    if (bl?.framebufferWidth && bl.framebufferHeight) {
      const refined = refineByEyeBuffer(this.headset, bl.framebufferWidth / 2 / scale, bl.framebufferHeight / scale);
      if (refined !== this.headset) { this.headset = refined; this.profile = headsetProfile(refined); }
    }
    this.startLevel = this.boot.level ?? this.profile.level;
    this.level = this.startLevel;
    this.foveation = this.startFoveation = this.boot.foveation;
    this.hz = this.startHz = Math.round(session?.frameRate ?? this.boot.targetHz) || this.boot.targetHz;
    this.gov = governorInit();
    this.govAcc = 0;
    this.warmS = 0;
    this.lastFrameT = 0;
    this.lastAction = null;
    this.actions = 0;

    // #3 — the VR profile onto the live settings; consumers rescale on the quality-changed emit.
    applyVrPreset(live, this.saved.preset, this.level);
    this.reapply();
    resetXrPerf();
    setXrPerfContext({ targetHz: this.hz, level: this.level, foveation: this.foveation, framebufferScale: scale, headset: this.profile.label });
    console.info(`[XRGov] ${this.profile.label}: level ${VR_LEVEL_NAMES[this.level]}, scale ${scale}, foveation ${this.foveation}, ${this.hz} Hz, ${vrChanges(this.saved.preset, this.level).length} settings changed`);
    bus.emit('atlas:status', { text: `VR profile: ${this.profile.label} · ${VR_LEVEL_NAMES[this.level]}` });
  }

  private restore(): void {
    const saved = this.saved;
    if (!saved) return;
    this.saved = null;
    this.cameraClampPending = false;

    const r = this.app.renderer.native;
    r.autoClear = saved.autoClear;
    const cam = this.camera();
    if (cam) {
      cam.near = saved.near;
      cam.far = saved.far;
      cam.updateProjectionMatrix();
    }
    // Every preset field back EXACTLY (keys the desktop never carried are deleted), then rebuild.
    restorePreset(activePreset(), saved.preset);
    setPersistOverride(null);
    this.reapply();
  }

  // ─── Performance + the governor ─────────────────────────────────────────────

  private tickPerf(dt: number): void {
    const now = performance.now();
    const info = (this.app.renderer.native as unknown as { info?: { render?: { drawCalls?: number; triangles?: number } } }).info?.render;
    // While the system menu (or a permission prompt) is up the session is 'visible-blurred' and the runtime throttles
    // its frames: nothing the app does makes those intervals short, so they are neither recorded nor judged.
    const vis = (this.session() as unknown as { visibilityState?: string } | null)?.visibilityState;
    if (vis && vis !== 'visible') { this.lastFrameT = 0; return; }
    const wallS = this.lastFrameT > 0 ? Math.min(1, (now - this.lastFrameT) / 1000) : 0;
    if (this.lastFrameT > 0) recordXrFrame(now - this.lastFrameT, info?.drawCalls ?? NaN, info?.triangles ?? NaN);
    this.lastFrameT = now;
    if (!this.boot.governor) return;
    this.warmS += wallS;
    if (this.warmS < GOV.WARMUP_S) return; // compile-dominated frames are not the scene's cost
    // The governor's holds (2 s over, 10 s of headroom) are WALL seconds — the same clock the frame intervals it
    // judges are measured on. The engine clamps a slow frame's dt (five fixed steps), which stretched every hold.
    void dt;
    this.govAcc += wallS;
    if (this.govAcc < GOV_EVERY_S) return;
    const step = this.govAcc;
    this.govAcc = 0;
    const snap = xrPerfSnapshot();
    const session = this.session();
    const xr = this.app.renderer.native.xr as unknown as { _glProjLayer?: unknown };
    const action = governorStep(this.gov, step, {
      frames: snap.frames, p95Ms: snap.p95Ms, missShare: snap.missShare, budgetMs: snap.budgetMs,
      foveation: this.foveation, hz: this.hz, level: this.level,
      startFoveation: this.startFoveation, startHz: this.startHz, startLevel: this.startLevel,
      // three foveates the SCENE render only on the layers path; on the base-layer path it reaches the final copy alone.
      canFoveate: xr._glProjLayer != null,
      allowLevel: false, // a level change is the player's (the Perf page) — see xr_governor.allowLevel
      rates: session?.supportedFrameRates ? Array.from(session.supportedFrameRates) : [],
    });
    if (action) this.act(action);
  }

  private act(a: GovernorAction): void {
    this.actions++;
    this.lastAction = { ...a, at: Date.now() };
    console.info(`[XRGov] ${a.kind} → ${a.value} (${a.why})`);
    if (a.kind === 'foveation') {
      this.foveation = a.value;
      (this.app.renderer.native.xr as unknown as XRish).setFoveation?.(a.value);
      setXrPerfContext({ foveation: a.value });
    } else if (a.kind === 'hz') {
      const session = this.session();
      const want = a.value;
      session?.updateTargetFrameRate?.(want).then(
        () => { this.hz = want; setXrPerfContext({ targetHz: want }); },
        (err: unknown) => console.warn(`[XRGov] updateTargetFrameRate(${want}) failed: ${String(err)}`),
      );
    } else {
      this.setLevel(a.value, a.why);
    }
    resetXrPerf();
  }

  private setLevel(level: VrLevel, why: string): void {
    const saved = this.saved;
    if (!saved || level === this.level) return;
    this.level = level;
    applyVrPreset(activePreset(), saved.preset, level);
    this.reapply();
    this.warmS = 0; resetXrPerf(); // the rebuild's frames are compiles, not the new level's cost
    setXrPerfContext({ level });
    bus.emit('atlas:status', { text: `VR quality: ${VR_LEVEL_NAMES[level]} (${why})` });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  private session(): XRSessionLike | null {
    try { return (this.app.renderer.native.xr as unknown as XRish).getSession?.() ?? null; } catch { return null; }
  }

  private camera(): THREE.PerspectiveCamera | null {
    const cam = atlas.localMesh?.children[0];
    return cam && (cam as THREE.PerspectiveCamera).isPerspectiveCamera ? (cam as THREE.PerspectiveCamera) : null;
  }

  private clampCamera(): void {
    const cam = this.camera();
    if (!cam) return; // rig not built yet — the per-frame system retries
    cam.near = VR_NEAR;
    cam.updateProjectionMatrix();
    this.cameraClampPending = false;
  }

  /** Rebuild post + fan out atlas:quality-changed / atlas:environment-updated. */
  private reapply(): void {
    const post = this.app.getModule('Post') as PostModule | undefined;
    if (post) {
      post.applySettings();
    } else {
      // Headless/verify environments without the Post module: fan out directly.
      bus.emit('atlas:quality-changed', {});
      bus.emit('atlas:environment-updated', {});
    }
  }

  dispose(): void {
    // Mid-session teardown must not leave VR overrides on the desktop state.
    this.restore();
    delete (globalThis as Record<string, unknown>).__vrOpt;
  }
}
